import { NcrRef, POLineGRNSummaryRow } from '../../lib/api';
import { countsAsOnTime } from '../../lib/onTimeStatus';

/**
 * Customer review 11 Sep (item 30c): the tooltip on an NCR-derived
 * rejection — "Includes 1 from NCR 3252".
 */
export const ncrTooltip = (qty: string | undefined, refs: NcrRef[] | undefined): string | undefined => {
  if (!refs?.length || !(parseFloat(qty ?? '') > 0)) return undefined;
  return `Includes ${parseFloat(qty ?? '')} from ${refs.map((r) => `NCR ${r.ncr_number}`).join(', ')}`;
};

/** The NCR-inclusive figures, falling back to the stored ones on a payload without them. */
export const acceptedOf = (g: POLineGRNSummaryRow) => g.effective_accepted_qty ?? g.quantity_accepted;
export const rejectedOf = (g: POLineGRNSummaryRow) => g.effective_rejected_qty ?? g.quantity_rejected;

/**
 * CR-7415: the date this delivery's on-time verdict was judged against — the
 * engine's anchor, else the line's ODD on a payload without one. Null when
 * neither is known, so the cell shows a dash and the row sorts last.
 */
export const oddOf = (g: POLineGRNSummaryRow): string | null =>
  g.verdict_anchor_date ?? g.original_delivery_date ?? null;

/** CR-7415: the on-time label as the badge prints it; null (sorts last) when unjudged. */
const onTimeLabelOf = (g: POLineGRNSummaryRow): string | null =>
  g.on_time_status ? (g.on_time_status_display || g.on_time_status) : null;

/** Module scope: the sort hook memoises on this object's identity (CR-7263). */
export const GRN_SORT_KEYS = {
  effective_accepted_qty: acceptedOf,
  effective_rejected_qty: rejectedOf,
  original_required_date: oddOf,
  on_time_status: onTimeLabelOf,
} as const;

export interface LineOtd {
  /** Deliveries whose verdict passes: On Time, or Early / Late overridden. */
  onTime: number;
  /** Deliveries that carry a verdict at all. */
  judged: number;
  /** onTime / judged × 100, unrounded; null when nothing has been judged. */
  pct: number | null;
}

/**
 * CR-7415 AC3: the line's on-time delivery % — deliveries on time over
 * deliveries with a verdict. A delivery nobody has judged yet is left out of
 * BOTH counts, so a line with no verdict at all has no figure (null), never 0%.
 * "On time" is the platform's passing set (`countsAsOnTime`): an overridden
 * Early / Late scores as a pass, a plain Early or Late counts against.
 *
 * @param rows - the line's GRN rows from `grn_summary`
 * @returns the counts and the unrounded percentage; round it for display with
 *   `formatKpiPercent`, as the dashboard OTD does
 */
export function lineOtd(rows: readonly POLineGRNSummaryRow[]): LineOtd {
  const judged = rows.filter((g) => !!g.on_time_status);
  const onTime = judged.filter((g) => countsAsOnTime(g.on_time_status)).length;
  return {
    onTime,
    judged: judged.length,
    pct: judged.length ? (onTime / judged.length) * 100 : null,
  };
}
